import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import en from '@angular/common/locales/en';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

import { provideStore } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { routes } from './app.routes';
import { icons } from './icons-provider';

import {
  ALERTS_AND_NOTIFICATIONS_PROVIDERS,
  ASSETS_PROVIDERS,
  AUTH_PROVIDERS,
  CHAT_SETTINGS_PROVIDERS,
  COMMON_PROVIDERS,
  CONFIGURATION_AUDIT_LOG_PROVIDERS,
  DATA_SOURCE_PROVIDERS,
  HANDOVER_TAKEOVER_PROVIDERS,
  HTTP_PROVIDERS,
  SCHEDULE_PROVIDERS,
  SECTOR_PROVIDERS,
  SECTOR_TASK_GROUPING_PROVIDERS,
  SHIFT_RESOURCE_PROVIDERS,
  SUB_TASK_PROVIDERS,
  TASK_DEPENDENCE_PROVIDERS,
  TASK_GROUP_TYPE_PROVIDERS,
  TASK_TEMPLATE_PROVIDERS,
  USER_MANAGEMENT_PROVIDERS,
  VESSEL_ACTIVITY_PROVIDERS,
  VESSEL_INFO_PROVIDERS,
  VESSEL_LANDING_PROVIDERS,
} from '@infrastructure/providers';

import { systemReducer } from '@state/system/system.reducer';
import { menuInitializer } from './infrastructure/providers/menu-initial.provider';
import { REPORT_PROVIDER } from './infrastructure/providers/report.providers';

const ngZorroConfig: NzConfig = {
  message: { nzTop: 120 },
  notification: { nzTop: 240 },
  theme: {
    primaryColor: 'red',
  },
};

registerLocaleData(en);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideStore<AppState>({
      system: systemReducer,
    }),

    provideRouter(
      routes,
      // withHashLocation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withPreloading(PreloadAllModules)
    ),
    provideClientHydration(withEventReplay()),

    // ng-zoro
    provideNzIcons(icons),
    provideNzI18n(en_US),
    provideNzConfig(ngZorroConfig),

    importProvidersFrom(FormsModule),
    provideAnimationsAsync(),
    importProvidersFrom(NzModalModule),

    provideHttpClient(withInterceptorsFromDi()),
    // Providers: always load last
    ...HTTP_PROVIDERS,
    // Configure default options for DatePipe to use Singapore timezone
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        // timezone: '+0800',
        dateFormat: 'dd/MM/yyyy HH:mm',
      },
    },
    ...AUTH_PROVIDERS,
    ...ALERTS_AND_NOTIFICATIONS_PROVIDERS,
    ...TASK_TEMPLATE_PROVIDERS,
    ...TASK_GROUP_TYPE_PROVIDERS,
    ...ASSETS_PROVIDERS,
    ...CONFIGURATION_AUDIT_LOG_PROVIDERS,
    ...COMMON_PROVIDERS,
    ...DATA_SOURCE_PROVIDERS,
    ...SECTOR_PROVIDERS,
    ...SECTOR_TASK_GROUPING_PROVIDERS,
    ...SHIFT_RESOURCE_PROVIDERS,
    ...VESSEL_ACTIVITY_PROVIDERS,
    ...VESSEL_INFO_PROVIDERS,
    ...VESSEL_LANDING_PROVIDERS,
    ...TASK_DEPENDENCE_PROVIDERS,
    ...SUB_TASK_PROVIDERS,
    ...CHAT_SETTINGS_PROVIDERS,
    ...HANDOVER_TAKEOVER_PROVIDERS,
    ...REPORT_PROVIDER,
    ...SCHEDULE_PROVIDERS,
    ...USER_MANAGEMENT_PROVIDERS,

    provideAppInitializer(menuInitializer),
  ],
};
